import { type RefObject, useCallback, useMemo } from 'react';

import type { Table } from '@tanstack/react-table';
import {
	defaultRangeExtractor,
	type Range,
	useVirtualizer,
	type VirtualItem,
} from '@tanstack/react-virtual';

import type { CharlieTableProps } from '../../charlie-table.types';

export type ColumnVirtualizer =
	| {
			virtualColumns: VirtualItem[];
			virtualPaddingLeft: number;
			virtualPaddingRight: number;
	  }
	| undefined;

export const useCharlieColumnVirtualization = <T extends object>({
	enableColumnVirtualization,
	enableColumnPinning,
	table,
	tableContainerRef,
	columnVirtualizerOptions,
}: {
	enableColumnVirtualization: boolean;
	enableColumnPinning: boolean;
	table: Table<T>;
	tableContainerRef: RefObject<HTMLDivElement>;
	columnVirtualizerOptions?: CharlieTableProps<T>['columnVirtualizerOptions'];
}): ColumnVirtualizer => {
	/**
	 * This theoretically breaks the rule of hooks but the prop can't be changed after the table is rendered
	 * so in practice it's fine
	 */
	if (!enableColumnVirtualization) {
		return undefined;
	}

	const { columnPinning, columnVisibility } = table.getState();
	const visibleColumns = table.getVisibleLeafColumns();

	const customColumnVirtualizerOptions =
		typeof columnVirtualizerOptions === 'function'
			? columnVirtualizerOptions({ table })
			: columnVirtualizerOptions;

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const [leftPinnedIndexes, rightPinnedIndexes] = useMemo(
		() =>
			enableColumnPinning
				? [
						table.getLeftVisibleLeafColumns().map((c) => c.getPinnedIndex()),
						table
							.getRightVisibleLeafColumns()
							.map((column) => visibleColumns.length - column.getPinnedIndex() - 1)
							.sort((a, b) => a - b),
					]
				: [[], []],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[columnPinning, columnVisibility, enableColumnPinning],
	);

	const numPinnedLeft = leftPinnedIndexes.length;
	const numPinnedRight = rightPinnedIndexes.length;

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const columnVirtualizer = useVirtualizer({
		count: visibleColumns.length,
		estimateSize: (index) => visibleColumns[index].getSize(),
		getScrollElement: () => tableContainerRef.current,
		horizontal: true,
		overscan: 5,
		// eslint-disable-next-line react-hooks/rules-of-hooks
		rangeExtractor: useCallback(
			(range: Range) => {
				const newIndexes = defaultRangeExtractor(range);
				if (!numPinnedLeft && !numPinnedRight) {
					return newIndexes;
				}
				// need to ensure that the pinned columns are always included
				return [...new Set([...leftPinnedIndexes, ...newIndexes, ...rightPinnedIndexes])];
			},
			[leftPinnedIndexes, rightPinnedIndexes, numPinnedLeft, numPinnedRight],
		),
		...customColumnVirtualizerOptions,
	});

	const virtualColumns = columnVirtualizer.getVirtualItems();
	const numColumns = virtualColumns.length;
	let virtualPaddingLeft = 0;
	let virtualPaddingRight = 0;

	// need to calculate the padding to take into account the pinned columns
	if (numColumns) {
		const totalSize = columnVirtualizer.getTotalSize();

		const leftNonPinnedStart = virtualColumns[numPinnedLeft]?.start || 0;
		const leftNonPinnedEnd = virtualColumns[leftPinnedIndexes.length - 1]?.end || 0;

		const rightNonPinnedStart = virtualColumns[numColumns - numPinnedRight]?.start || 0;
		const rightNonPinnedEnd = virtualColumns[numColumns - numPinnedRight - 1]?.end || 0;

		virtualPaddingLeft = leftNonPinnedStart - leftNonPinnedEnd;

		virtualPaddingRight =
			totalSize - rightNonPinnedEnd - (numPinnedRight ? totalSize - rightNonPinnedStart : 0);
	}

	return { virtualColumns: virtualColumns ?? undefined, virtualPaddingLeft, virtualPaddingRight };
};
